import {
  FormLabel,
  FormControl,
  Autocomplete,
  ListItem,
  Checkbox,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { AutocompleteFiltersData } from "../../core/models/supplies/supplies.model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { filterOptions } from "../../utils/components/supplies/supplies.util";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import { FiltersCifCups } from "../../core/models/filtersCifCups/filtersCifCups.model";
import "./FilterCifCups.scss";
import { FiltersBills } from "../../core/models/bills/bills.model";
import { formatTariffOptionLabel } from "../../utils/formatters/formatters.util";

interface FiltersCifCupsProps {
  isFiltersOpen: boolean;
  ownerDataCups: OwnerDataCups[];
  filters: FiltersCifCups | FiltersBills;
  drawerFilters: FiltersCifCups | FiltersBills;
  setDrawerFilters: React.Dispatch<
    React.SetStateAction<FiltersCifCups | FiltersBills>
  >;
  firstInputChanged: string;
  setFirstInputChanged: React.Dispatch<React.SetStateAction<string>>;
  tariffOptions?: string[];
}

const FilterCifCups = ({
  drawerFilters,
  setDrawerFilters,
  isFiltersOpen,
  filters,
  ownerDataCups,
  firstInputChanged,
  setFirstInputChanged,
  tariffOptions,
}: FiltersCifCupsProps) => {
  const { t } = useTranslation();
  const [optionsFilters, setOptionsFilters] = useState<FiltersCifCups>({
    cups: [],
    cif: [],
    socialReason: [],
    tariffs: tariffOptions,
  });
  const [hasCifCupsChanged, setHasCifCupsChanged] = useState<boolean>(true);
  const cupsOptions = useMemo(
    () => [...new Set(ownerDataCups.map(({ codeCups }) => codeCups))],
    [ownerDataCups]
  );
  const cifOptions = useMemo(
    () => [...new Set(ownerDataCups.map(({ cif }) => cif))],
    [ownerDataCups]
  );
  const socialReasonOptions = useMemo(
    () => [
      ...new Set(
        ownerDataCups
          .map(({ socialReason }) => socialReason)
          .filter((socialReason) => socialReason)
      ),
    ],
    [ownerDataCups]
  );

  const checkFistInputToChange = useCallback(
    (cif: string[], cups: string[], socialReason: string[]) => {
      if (cif.length && !cups.length && !socialReason.length) {
        setFirstInputChanged("cif");
        return "cif";
      }
      if (cups.length && !cif.length && !socialReason.length) {
        setFirstInputChanged("cups");
        return "cups";
      }
      if (socialReason.length && !cif.length && !cups.length) {
        setFirstInputChanged("socialReason");
        return "socialReason";
      }
    },
    [setFirstInputChanged]
  );

  useEffect(() => {
    if (hasCifCupsChanged && ownerDataCups.length) {
      const { cif, cups, socialReason } = drawerFilters;
      const firstInputToChange = checkFistInputToChange(
        cif,
        cups,
        socialReason
      );

      const ifCifFirstInputChanged =
        firstInputToChange === "cif" || firstInputChanged === "cif";
      const ifCupsFirstInputChanged =
        firstInputToChange === "cups" || firstInputChanged === "cups";
      const ifSocialReasonFirstInputChanged =
        firstInputToChange === "socialReason" ||
        firstInputChanged === "socialReason";

      const filteredCups = filterOptions(
        ownerDataCups,
        ["cif", "socialReason"],
        "codeCups",
        [cif, socialReason]
      );
      const filteredCif = filterOptions(
        ownerDataCups,
        ["codeCups", "socialReason"],
        "cif",
        [cups, socialReason]
      );
      const filteredSocialReason = filterOptions(
        ownerDataCups,
        ["codeCups", "cif"],
        "socialReason",
        [cups, cif]
      );

      setOptionsFilters((prev) => ({
        tariffs: prev.tariffs,
        cups:
          ifCifFirstInputChanged || ifSocialReasonFirstInputChanged
            ? [...new Set(filteredCups)]
            : cupsOptions,
        cif:
          ifCupsFirstInputChanged || ifSocialReasonFirstInputChanged
            ? [...new Set(filteredCif)]
            : cifOptions,
        socialReason:
          ifCifFirstInputChanged || ifCupsFirstInputChanged
            ? [...new Set(filteredSocialReason)]
            : socialReasonOptions,
      }));

      setHasCifCupsChanged(false);
    }
  }, [
    checkFistInputToChange,
    cifOptions,
    cupsOptions,
    socialReasonOptions,
    drawerFilters,
    firstInputChanged,
    hasCifCupsChanged,
    ownerDataCups,
  ]);

  useEffect(() => {
    if (isFiltersOpen) {
      setDrawerFilters(filters);
      setHasCifCupsChanged(true);
    }
  }, [filters, isFiltersOpen, setDrawerFilters]);

  const autocompleteFiltersData: AutocompleteFiltersData[] = [
    {
      id: "cif",
      label: "CIF",
      placeholder: "Todos los CIF",
      limitTags: 2,
    },
    {
      id: "tariffs",
      label: "Tarifas",
      placeholder: "Todas las tarifas",
      limitTags: 2,
    },
    {
      id: "cups",
      label: "CUPS",
      placeholder: "Todos los CUPS",
      limitTags: 1,
    },
    {
      id: "socialReason",
      label: "Razón social",
      placeholder: "Todas las razones sociales",
      limitTags: 1,
    },
  ];

  return (
    <Stack className="filters-cif-cups">
      {autocompleteFiltersData.map(({ id, label, placeholder, limitTags }) => {
        const options = optionsFilters[id];
        const value = drawerFilters[id];
        if (options === undefined || value === undefined) return null;

        const isDisabled = !!!options.length;

        return (
          <FormControl key={id} fullWidth>
            <FormLabel
              sx={{
                fontWeight: "600",
                paddingBottom: ".8rem",
                color: isDisabled ? "#A7B8BF" : "",
              }}
            >
              {t(`Tablas.${label}`)}
            </FormLabel>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={isDisabled}
              options={options}
              value={value}
              noOptionsText={t("Tablas.No hay coincidencias")}
              onChange={(event, value, reason) => {
                setHasCifCupsChanged(true);
                setDrawerFilters((prevDrawerFilters) => {
                  return {
                    ...prevDrawerFilters,
                    [id]: value,
                  };
                });
              }}
              renderOption={(props, option, { selected }) => (
                <ListItem {...props}>
                  <Checkbox
                    icon={<span className="t-icon-checkbox-default" />}
                    checkedIcon={
                      <span className="t-icon t-icon-checkbox-checked" />
                    }
                    sx={{ fontSize: "24px" }}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {id === "tariffs" ? formatTariffOptionLabel(option) : option}
                </ListItem>
              )}
              renderTags={(value, getTagProps) => (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={
                        <div
                          style={
                            id === "socialReason"
                              ? {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 200,
                                }
                              : undefined
                          }
                        >
                          {id === "tariffs"
                            ? formatTariffOptionLabel(option)
                            : option}
                        </div>
                      }
                    />
                  ))}
                  {value.length > limitTags && ` +${value.length - limitTags}`}
                </>
              )}
              style={{
                width: "100%",
              }}
              renderInput={(params) => (
                <TextField
                  sx={{
                    width: "100%",
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: {
                      boxShadow: "none",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    fullWidth: true,
                    endAdornment: (
                      <Typography
                        component="span"
                        className="t-custom-dropdown t-icon-arrow-down"
                        data-testid="dropdown-arrow"
                        sx={{
                          color: isDisabled ? "#A7B8BF" : "primary.main",
                          position: "absolute",
                          right: "12px",
                          pointerEvents: "none",
                        }}
                      />
                    ),
                  }}
                  placeholder={t(
                    `Tablas.${value.length ? label : placeholder}`
                  )}
                />
              )}
            />
          </FormControl>
        );
      })}
    </Stack>
  );
};

export default FilterCifCups;
